import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPaperPlane, FaCheck, FaSpinner, FaGift } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../../api/apiClient';

/**
 * Composant de formulaire d'inscription à la newsletter
 * Peut être utilisé dans le footer, popup, ou page dédiée
 */
const NewsletterForm = ({ 
  variant = 'default', // 'default', 'compact', 'hero'
  source = 'homepage',
  showRewards = true,
  className = ''
}) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Veuillez entrer votre adresse email');
      return;
    }

    // Validation email basique
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Adresse email invalide');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/newsletter/subscribe', {
        email,
        firstName,
        source,
        language: i18n.language?.split('-')[0] || 'fr'
      });

      if (response.data.success) {
        setSuccess(true);
        setEmail('');
        setFirstName('');
        
        if (response.data.isAlreadySubscribed) {
          toast.info('Vous êtes déjà inscrit à notre newsletter !');
        } else if (response.data.isResubscribe) {
          toast.success('Bienvenue de nouveau ! Vous êtes réabonné.');
        } else {
          toast.success('Merci pour votre inscription ! 🎉');
        }
      }
    } catch (err) {
      console.error('Erreur newsletter:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'inscription');
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  // Variante compacte (footer)
  if (variant === 'compact') {
    return (
      <div className={`${className}`}>
        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2 text-green-400"
          >
            <FaCheck />
            <span>Inscrit avec succès !</span>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email"
              className="flex-1 px-4 py-2 rounded-lg bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-primary"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
              <span className="hidden sm:inline">S'inscrire</span>
            </button>
          </form>
        )}
        {error && <p className="text-red-400 text-sm mt-1">{error}</p>}
      </div>
    );
  }

  // Variante hero (grande section)
  if (variant === 'hero') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-2xl p-8 border border-primary/20 ${className}`}
      >
        <div className="text-center mb-6">
          <FaEnvelope className="text-4xl text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">
            Restez informé !
          </h3>
          <p className="text-slate-300">
            Recevez nos actualités, offres exclusives et mises à jour BBT
          </p>
        </div>

        {showRewards && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-primary mb-2">
              <FaGift />
              <span className="font-semibold">Bonus inscription</span>
            </div>
            <p className="text-sm text-slate-300 text-center">
              Recevez <span className="text-primary font-bold">50 BBT</span> offerts à votre première transaction !
            </p>
          </div>
        )}

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheck className="text-3xl text-green-400" />
            </div>
            <h4 className="text-xl font-bold text-white mb-2">Merci !</h4>
            <p className="text-slate-300">Vous êtes maintenant inscrit à notre newsletter.</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Votre prénom (optionnel)"
                className="w-full px-4 py-3 rounded-lg bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                disabled={loading}
              />
            </div>
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre adresse email *"
                className="w-full px-4 py-3 rounded-lg bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-primary"
                disabled={loading}
                required
              />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Inscription...
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  S'inscrire à la newsletter
                </>
              )}
            </button>
            <p className="text-xs text-slate-500 text-center">
              En vous inscrivant, vous acceptez de recevoir nos emails promotionnels (1/mois max).
              <br />Désabonnement possible à tout moment.
            </p>
          </form>
        )}
      </motion.div>
    );
  }

  // Variante par défaut
  return (
    <div className={`bg-slate-800/50 rounded-xl p-6 border border-slate-700 ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <FaEnvelope className="text-2xl text-primary" />
        <h4 className="text-lg font-semibold text-white">Newsletter</h4>
      </div>
      
      {success ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-2 text-green-400 py-4"
        >
          <FaCheck />
          <span>Inscription réussie !</span>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre email"
            className="w-full px-4 py-2 rounded-lg bg-slate-700/50 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-primary"
            disabled={loading}
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
            S'inscrire
          </button>
        </form>
      )}
    </div>
  );
};

export default NewsletterForm;

